import React, { useState, useEffect } from 'react';
import { X, Clock, Download, Eye, RefreshCw } from 'lucide-react';
import { renderApi } from '../../services/api';
import toast from 'react-hot-toast';

interface Version {
  id: string;
  timestamp: Date;
  filename: string;
  url: string;
  size: number;
  userEmail?: string;
}

interface VersioningModalProps {
  isOpen: boolean;
  onClose: () => void;
}

export const VersioningModal: React.FC<VersioningModalProps> = ({ 
  isOpen, 
  onClose
}) => {
  const [versions, setVersions] = useState<Version[]>([]);
  const [loading, setLoading] = useState(false);
  const [applyingVersion, setApplyingVersion] = useState<string | null>(null);
  const [showAll, setShowAll] = useState(false);

  const loadVersions = async () => {
    try {
      setLoading(true);
      const limit = showAll ? 100 : 10;
      const response = await renderApi.listVersions(limit);
      
      if (response.success) {
        setVersions(response.versions);
      } else {
        toast.error('Erro ao carregar versões');
      }
    } catch (error) {
      console.error('Erro ao carregar versões:', error);
      toast.error('Erro ao carregar versões');
    } finally {
      setLoading(false);
    }
  };

  const applyVersion = async (versionId: string) => {
    try {
      setApplyingVersion(versionId);
      
      const response = await renderApi.applyVersion(versionId);
      
      if (response.success) {
        toast.success(`Versão ${versionId} aplicada com sucesso!`);
        onClose();
        // Recarregar a página para atualizar o preview
        window.location.reload();
      } else {
        toast.error('Erro ao aplicar versão');
      }
    } catch (error) {
      console.error('Erro ao aplicar versão:', error);
      toast.error('Erro ao aplicar versão');
    } finally {
      setApplyingVersion(null);
    }
  };

  const formatDate = (date: Date) => {
    return new Intl.DateTimeFormat('pt-BR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit'
    }).format(new Date(date));
  };

  const formatFileSize = (bytes: number) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  useEffect(() => {
    if (isOpen) {
      loadVersions();
    }
  }, [isOpen, showAll]);

  const toggleShowAll = () => {
    setShowAll(!showAll);
    loadVersions(); // Recarrega com o novo limite
  };

  if (!isOpen) return null;

  return (
    <div className="modal-backdrop flex items-center justify-center z-[9999]">
      <div className="glass-modal modal-enter rounded-xl max-w-4xl w-full mx-4 max-h-[90vh] overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b">
          <div className="flex items-center space-x-3">
            <Clock className="w-6 h-6 text-primary-500" />
            <div>
              <h3 className="text-lg font-semibold text-gray-900">
                Histórico de Versões
              </h3>
              <p className="text-sm text-gray-600">
                {showAll ? 'Todas as versões (máximo 100)' : 'Últimas 10 versões'}
              </p>
            </div>
          </div>
          <div className="flex items-center space-x-2">
            <button
              onClick={toggleShowAll}
              disabled={loading}
              className="px-3 py-1.5 text-sm bg-gray-100 text-gray-700 rounded-md hover:bg-gray-200 transition-colors disabled:opacity-50"
              title={showAll ? "Ver últimas 10 versões" : "Ver todas as versões"}
            >
              {showAll ? "Últimas 10" : "Ver Todas"}
            </button>
            <button
              onClick={loadVersions}
              disabled={loading}
              className="p-2 hover:bg-gray-100 rounded-md transition-colors disabled:opacity-50"
              title="Recarregar"
            >
              <RefreshCw className={`w-5 h-5 text-gray-500 ${loading ? 'animate-spin' : ''}`} />
            </button>
            <button
              onClick={onClose}
              className="p-2 hover:bg-gray-100 rounded-md transition-colors"
            >
              <X className="w-5 h-5 text-gray-500" />
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="p-4 overflow-y-auto max-h-[calc(90vh-120px)]">
          {loading ? (
            <div className="flex items-center justify-center h-32">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-500"></div>
            </div>
          ) : versions.length === 0 ? (
            <div className="text-center py-8">
              <Clock className="w-12 h-12 text-gray-300 mx-auto mb-2" />
              <p className="text-sm text-gray-500">Nenhuma versão encontrada</p>
              <p className="text-xs text-gray-400 mt-1">
                As versões aparecem aqui após publicar o site
              </p>
            </div>
          ) : (
            <div className="space-y-3">
              {versions.map((version) => (
                <div
                  key={version.id}
                  className="border rounded-md p-4 hover:bg-gray-50 transition-colors"
                >
                  <div className="flex items-center justify-between">
                    <div className="flex-1">
                      <div className="flex items-center space-x-3">
                        <div className={`${version.userEmail?.includes('(Agendamento)') ? 'bg-yellow-100 text-yellow-700' : 'bg-primary-100 text-primary-700'} px-2 py-1 rounded text-xs font-medium`}>
                          {version.id}
                        </div>
                        <div className="text-sm text-gray-600">
                          {formatDate(version.timestamp)}
                        </div>
                        <div className="text-xs text-gray-400">
                          {formatFileSize(version.size)}
                        </div>
                      </div>
                      {version.userEmail && version.userEmail !== 'unknown' && (
                        <div className="mt-1 text-xs text-gray-400">
                          Publicado por: {version.userEmail}
                        </div>
                      )}
                    </div>
                    
                    <div className="flex items-center space-x-2">
                      <button
                        onClick={() => window.open(version.url, '_blank')}
                        className="p-2 hover:bg-gray-100 rounded-md transition-colors"
                        title="Visualizar JSON"
                      >
                        <Eye className="w-4 h-4 text-gray-500" />
                      </button>
                      <button
                        onClick={() => applyVersion(version.id)}
                        disabled={applyingVersion === version.id}
                        className="flex items-center space-x-1 px-3 py-1 bg-gradient-primary text-white rounded-md hover:opacity-90 transition-colors disabled:opacity-50"
                        title="Aplicar esta versão"
                      >
                        {applyingVersion === version.id ? (
                          <div className="animate-spin rounded-full h-3 w-3 border-b-2 border-white"></div>
                        ) : (
                          <Download className="w-3 h-3" />
                        )}
                        <span className="text-xs">
                          {applyingVersion === version.id ? 'Aplicando...' : 'Aplicar'}
                        </span>
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between p-4 border-t bg-gray-50">
          <div className="text-sm text-gray-600">
            Total: {versions.length} vers{versions.length !== 1 ? 'ões' : 'ão'}
          </div>
          <div className="text-xs text-gray-400">
            As versões são salvas automaticamente a cada publicação
          </div>
        </div>
      </div>
    </div>
  );
};
